import { injectable } from 'inversify';
import { eachDayOfInterval } from 'date-fns';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip/ColoredChip.types';
import {
  CalendarDiff,
  CalendarGroupStatus,
  CalendarItemSupervisor,
  ScheduleUser,
  SchedulePercentsEntity,
} from '@vk-hr-tek/ui/Calendar/types';

import {
  Event as EventDetailEntity,
  EventListItem as EventListEntity,
  UnitScheduleItem,
  VacationItem,
  WorkCalendarDate,
  ScheduleEmployee,
  ScheduleEventListItem as VacationListEntity,
  UnitPlannedVacationsPercent,
} from '@app/gen/events';

import { VACATION_START_ATTR_ID, VACATION_END_ATTR_ID } from '../../constants';

const colors: Record<string, ChipColorUnion> = {
  completed: 'green',
  accept: 'purple',
  new: 'blue',
  sync: 'orange',
  cancelled: 'red',
  default: 'gray',
};

@injectable()
export class VacationsMapper {
  constructor(
    private format: FormatService,
    private calendar: Calendar,
  ) {}

  processEmployeeShortName(employee: ScheduleEmployee) {
    const firstInitial = `${employee.first_name[0]}.`;
    const middleInitial = employee.middle_name
      ? `${employee.middle_name[0]}.`
      : '';

    return `${employee.second_name} ${firstInitial}${middleInitial}`;
  }

  processEmployee(vacation: VacationListEntity) {
    return {
      id: vacation.id,
      fullName:
        `${vacation.employee.second_name} ${vacation.employee.first_name} ${vacation.employee.middle_name}`.trim(),
      shortName: this.processEmployeeShortName(vacation.employee),
      name: `${vacation.employee.second_name} ${vacation.employee.first_name}`,
      avatar: `${vacation.employee.first_name[0]}${vacation.employee.second_name[0]}`,
      personnelNumber: vacation.employee.personnel_number,
      status: vacation.status,
      position: vacation.employee.position,
    };
  }

  processUnitName(units?: UnitScheduleItem[], unitId?: string) {
    return (
      (units && unitId && units?.find(({ id }) => unitId === id)?.name) || ''
    );
  }

  processCalendar(dates: WorkCalendarDate[] = []): CalendarDiff {
    return dates.reduce((acc: CalendarDiff, curr) => {
      acc[curr.date] = {
        workday: curr.type === 'workday',
        holiday: curr.type === 'holiday',
      };

      return acc;
    }, {});
  }

  processDuration(start: Date, end: Date, calendarDiff: CalendarDiff) {
    const intervalDates = eachDayOfInterval({
      start,
      end,
    });

    return intervalDates.filter((date) => {
      const dateISO: string = this.calendar.formatISO(date, {
        representation: 'date',
      });

      const calendarDiffDate = calendarDiff[dateISO];

      if (calendarDiffDate) {
        return !calendarDiffDate.holiday;
      }

      return !this.calendar.isHoliday(date);
    }).length;
  }

  processDescribeVacations(
    vacation: VacationItem[],
    calendarDiff: CalendarDiff,
  ) {
    const countName = this.format.pluralize(vacation.length, [
      'отпуск',
      'отпуска',
      'отпусков',
    ]);

    const days = vacation.reduce((acc, { from_date, to_date }) => {
      const start = new Date(from_date);
      const end = new Date(to_date);

      acc += this.processDuration(start, end, calendarDiff);

      return acc;
    }, 0);

    return days > 0 ? `${countName}, ${days} дн.` : '-';
  }

  processUserActions(
    vacation: VacationListEntity,
    calendarDiff: CalendarDiff,
    team?: string,
  ): ScheduleUser {
    return {
      id: vacation.id,
      name: `${vacation.employee.second_name} ${vacation.employee.first_name} ${vacation.employee.middle_name}`,
      team,
      vacations: vacation.vacations.map(({ from_date, to_date }) => {
        const start = new Date(from_date);
        const end = new Date(to_date);

        const duration = this.processDuration(start, end, calendarDiff);

        return [
          duration,
          'дн.',
          this.format.toDate(from_date),
          '–',
          this.format.toDate(to_date),
        ].join(' ');
      }),
    };
  }

  processInitialName({
    first_name,
    middle_name,
    second_name,
  }: ScheduleEmployee) {
    const initial = (name: string) =>
      name[0] ? `${name[0].toUpperCase()}.` : '';

    return {
      fullName: `${second_name} ${first_name} ${middle_name}`.trim(),
      surnameWithInitials: `${second_name} ${initial(first_name)} ${initial(
        middle_name,
      )}`.trim(),
      initials: `${first_name[0]?.toUpperCase() ?? ''}${
        second_name[0]?.toUpperCase() ?? ''
      }`,
    };
  }

  processStage(status: VacationListEntity['status']) {
    switch (status) {
      case 'new':
        return 'Заполнение';
      case 'accept':
        return 'Согласование';
      case 'finished':
        return 'Завершено';
      case 'canceled':
        return 'Отменено';
      case 'onec_sync':
        return 'Запись данных';
      default:
        return status;
    }
  }

  processGroupStatuses(vacations: VacationListEntity[]): CalendarGroupStatus[] {
    const sortedStatuses: Record<VacationListEntity['status'], number> = {
      new: 0,
      accept: 0,
      onec_sync: 0,
      finished: 0,
      canceled: 0,
    };

    vacations.forEach(({ status }) => {
      sortedStatuses[status]++;
    });

    return Object.entries(sortedStatuses)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => ({
        status: this.processStage(status as VacationListEntity['status']),
        count,
      }));
  }

  processGroupStructure(units?: UnitScheduleItem[], rootId?: string): string[] {
    if (!units || !rootId) {
      return [];
    }

    const result: string[] = [];
    let nextId: string | undefined = rootId;

    const unitsMap = units?.reduce(
      (acc: Record<string, UnitScheduleItem>, curr) => {
        acc[curr.id] = curr;

        return acc;
      },
      {},
    );

    while (nextId && unitsMap[nextId]) {
      result.push(unitsMap[nextId].name);
      nextId = unitsMap[nextId].parent_id;
    }

    return result.reverse();
  }

  processGroupSupervisor(
    unit?: UnitScheduleItem,
  ): CalendarItemSupervisor | undefined {
    if (!unit || !unit.manager) {
      return;
    }

    const name = [
      unit.manager?.employee?.second_name || '',
      unit.manager?.employee?.first_name || '',
      unit.manager?.employee?.middle_name || '',
    ]
      .filter((el) => el)
      .join(' ');

    return {
      name,
      personalNumber: unit.manager?.employee?.personnel_number || '',
      type: unit.manager.type_name,
    };
  }

  processSchedulePercents(
    percentEntity?: UnitPlannedVacationsPercent,
  ): SchedulePercentsEntity | undefined {
    if (!percentEntity) return undefined;

    return {
      fewEmployees: percentEntity.few_employees,
      monthPercents: [...percentEntity.months]
        .sort((a, b) => a.month - b.month)
        .map(({ percent, threshold_exceeded }) => {
          return { percent, exceeded: threshold_exceeded };
        }),
    };
  }

  processStageList(status: VacationListEntity['status']) {
    const name = this.processStage(status);

    switch (status) {
      case 'new':
        return {
          name,
          color: colors.new,
        };
      case 'accept':
        return {
          name,
          color: colors.accept,
        };
      case 'finished':
        return {
          name,
          color: colors.completed,
        };
      case 'canceled':
        return {
          name,
          color: colors.cancelled,
        };
      case 'onec_sync':
        return {
          name,
          color: colors.sync,
        };
      default:
        return {
          name,
          color: colors.default,
        };
    }
  }

  processVacation(event: EventListEntity | EventDetailEntity) {
    const start = event.attributes.find(
      (attr) => attr.id === VACATION_START_ATTR_ID,
    );
    const end = event.attributes.find(
      (attr) => attr.id === VACATION_END_ATTR_ID,
    );

    const startDate = start ? new Date(start.value as string) : null;
    const endDate = end ? new Date(end.value as string) : null;
    let count: string | number = '';

    if (startDate && endDate) {
      count =
        startDate <= endDate
          ? this.calendar.countOfVacationDays(endDate, startDate)
          : 'Ошибка';
    }

    return {
      start: start ? this.format.toDate(start.value as string) : '',
      end: end ? this.format.toDate(end.value as string) : '',
      count,
      isHolidaysInVacationInterval: Boolean(
        startDate &&
        endDate &&
        startDate <= endDate &&
        this.calendar.countOfHolidaysInInterval(startDate, endDate),
      ),
    };
  }

  processDeadline(deadline: string) {
    const durationDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(deadline), 1),
    );

    return `${this.format.toTime(durationDate)} ${this.format.toDate(
      durationDate,
    )}`;
  }
}
